import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { HiCheck } from 'react-icons/hi';
import apiService from '../services/apiService';
import '../styles/portfolioMatrix.css';

// Componente de célula memoizado
const MatrixCell = React.memo(({ rowId, colId, isChecked, isDiagonal, onToggle, isHighlightedCell }) => {
    return (
        <td
            className={`portfolio-cell ${isDiagonal ? 'diagonal' : ''} ${isHighlightedCell ? 'cell-highlighted' : ''}`}
            onClick={onToggle}
        >
            <div className={`checkbox-wrapper ${isChecked ? 'checked' : ''}`}>
                {isChecked && <HiCheck className="check-icon" />}
            </div>
        </td>
    );
});

MatrixCell.displayName = 'MatrixCell';

// Componente de header de coluna memoizado
const ColumnHeader = React.memo(({ codigo }) => {
    return (
        <th className="portfolio-header">
            <div className="portfolio-header-text">{codigo}</div>
        </th>
    );
});

ColumnHeader.displayName = 'ColumnHeader';

// Componente de header de linha memoizado
const RowHeader = React.memo(({ codigo, nome, isSearchHighlighted }) => {
    return (
        <td
            className={`portfolio-row-header ${isSearchHighlighted ? 'search-highlighted' : ''}`}
            data-it-codigo={codigo}
        >
            <div className="portfolio-row-header-content">
                <span className="portfolio-id">{codigo}</span>
                <span className="portfolio-name" title={nome}>
                    {nome?.substring(0, 25)}
                    {nome?.length > 25 ? '...' : ''}
                </span>
            </div>
        </td>
    );
});

RowHeader.displayName = 'RowHeader';

const ITMatrixModal = ({ show, onHide, iniciativasTecnologicas, onSave }) => {
    const [selectionMatrix, setSelectionMatrix] = useState({});
    const [originalMatrix, setOriginalMatrix] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIT, setHighlightedIT] = useState(null);
    const [cellSearchTerm, setCellSearchTerm] = useState('');
    const [highlightedCell, setHighlightedCell] = useState({ row: null, col: null });

    const searchInputRef = useRef(null);
    const cellSearchInputRef = useRef(null);

    const itCodigos = useMemo(
        () => Object.keys(iniciativasTecnologicas).sort(),
        [iniciativasTecnologicas]
    );

    // Carregar matriz toda vez que o modal abrir
    useEffect(() => {
        if (show && itCodigos.length > 0) {
            setIsLoading(true);

            // Carregar a matriz padrão do backend
            apiService.obterITCorrelatos()
                .then(response => {
                    if (response.sucesso && response.itCorrelatos !== undefined) {
                        // Converter grupos em matriz de seleção
                        const initialMatrix = {};
                        itCodigos.forEach(codigo => {
                            initialMatrix[codigo] = {};
                            itCodigos.forEach(outroCodigo => {
                                initialMatrix[codigo][outroCodigo] = codigo === outroCodigo;
                            });
                        });

                        // Marcar correlações dos grupos
                        if (Array.isArray(response.itCorrelatos)) {
                            response.itCorrelatos.forEach(grupo => {
                                if (Array.isArray(grupo)) {
                                    grupo.forEach(codigo1 => {
                                        grupo.forEach(codigo2 => {
                                            if (initialMatrix[codigo1] && initialMatrix[codigo1][codigo2] !== undefined) {
                                                initialMatrix[codigo1][codigo2] = true;
                                            }
                                        });
                                    });
                                }
                            });
                        }

                        setSelectionMatrix(initialMatrix);
                        setOriginalMatrix(JSON.parse(JSON.stringify(initialMatrix)));
                        setIsInitialized(true);
                    } else {
                        inicializarMatrizVazia();
                        setIsInitialized(true);
                    }
                })
                .catch(() => {
                    inicializarMatrizVazia();
                    setIsInitialized(true);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [show, itCodigos]);

    const inicializarMatrizVazia = () => {
        const initialMatrix = {};
        itCodigos.forEach(codigo => {
            initialMatrix[codigo] = {};
            itCodigos.forEach(outroCodigo => {
                initialMatrix[codigo][outroCodigo] = codigo === outroCodigo;
            });
        });
        setSelectionMatrix(initialMatrix);
        setOriginalMatrix(JSON.parse(JSON.stringify(initialMatrix)));
    };

    // Converter matriz de seleção em lista de todas as correlações
    const convertMatrixToGroups = useCallback((matrix) => {
        const correlacoes = [];

        // Para cada linha da matriz, coletar todas as ITs correlacionadas
        itCodigos.forEach(codigo => {
            const correlacionados = [];

            itCodigos.forEach(outroCodigo => {
                // Incluir correlações (exceto diagonal)
                if (codigo !== outroCodigo && matrix[codigo][outroCodigo]) {
                    correlacionados.push(outroCodigo);
                }
            });

            // Se há correlações para esta IT, adicionar à lista
            if (correlacionados.length > 0) {
                correlacoes.push([codigo, ...correlacionados.sort()]);
            }
        });

        return correlacoes;
    }, [itCodigos]);

    const toggleSelection = useCallback((rowId, colId) => {
        if (rowId === colId) return;

        setSelectionMatrix(prev => {
            const newMatrix = { ...prev };
            const newValue = !prev[rowId][colId];
            newMatrix[rowId] = { ...prev[rowId], [colId]: newValue };
            newMatrix[colId] = { ...prev[colId], [rowId]: newValue };

            return newMatrix;
        });
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const grupos = convertMatrixToGroups(selectionMatrix);
            await onSave(grupos);
            setOriginalMatrix(JSON.parse(JSON.stringify(selectionMatrix)));
            onHide();
        } catch (error) {
            console.error('Erro ao salvar:', error);
            alert('Erro ao salvar configuração');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setSelectionMatrix(JSON.parse(JSON.stringify(originalMatrix)));
        setSearchTerm('');
        setCellSearchTerm('');
        setHighlightedIT(null);
        setHighlightedCell({ row: null, col: null });
        onHide();
    };

    const handleClearAll = useCallback(() => {
        setSelectionMatrix(prev => {
            const newMatrix = {};
            itCodigos.forEach(codigo => {
                newMatrix[codigo] = {};
                itCodigos.forEach(outroCodigo => {
                    newMatrix[codigo][outroCodigo] = codigo === outroCodigo;
                });
            });

            return newMatrix;
        });
    }, [itCodigos]);

    // Buscar IT automaticamente ao digitar
    useEffect(() => {
        const term = searchTerm.trim();

        if (!term) {
            setHighlightedIT(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            const foundCodigo = itCodigos.find(codigo => {
                const nome = iniciativasTecnologicas[codigo]?.toLowerCase() || '';
                const termLower = term.toLowerCase();
                return codigo.toLowerCase().includes(termLower) || nome.includes(termLower);
            });

            if (foundCodigo) {
                setHighlightedIT(foundCodigo);
                setTimeout(() => {
                    const element = document.querySelector(`[data-it-codigo="${foundCodigo}"]`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);
            } else {
                setHighlightedIT(null);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, itCodigos, iniciativasTecnologicas]);

    // Buscar célula específica
    useEffect(() => {
        const term = cellSearchTerm.trim();

        if (!term) {
            setHighlightedCell({ row: null, col: null });
            return;
        }

        const timeoutId = setTimeout(() => {
            const match = term.match(/^(.+?)\s*[x×,\s]\s*(.+?)$/i);

            if (match) {
                const rowCodigo = match[1].trim().toUpperCase();
                const colCodigo = match[2].trim().toUpperCase();

                if (itCodigos.includes(rowCodigo) && itCodigos.includes(colCodigo)) {
                    setHighlightedCell({ row: rowCodigo, col: colCodigo });

                    setTimeout(() => {
                        const scrollContainer = document.querySelector('.portfolio-matrix-scroll-container');
                        const rowElement = document.querySelector(`[data-it-codigo="${rowCodigo}"]`);

                        if (scrollContainer && rowElement) {
                            const rowRect = rowElement.getBoundingClientRect();
                            const containerRect = scrollContainer.getBoundingClientRect();

                            const colIndex = itCodigos.indexOf(colCodigo);
                            const cellWidth = 32;
                            const headerWidth = 140;

                            const rowOffsetTop = rowElement.offsetTop;
                            const targetScrollTop = rowOffsetTop - (containerRect.height / 2) + (rowRect.height / 2);

                            const targetScrollLeft = headerWidth + (colIndex * cellWidth) - (containerRect.width / 2) + (cellWidth / 2);

                            scrollContainer.scrollTo({
                                top: targetScrollTop,
                                left: targetScrollLeft,
                                behavior: 'smooth'
                            });
                        }
                    }, 100);
                } else {
                    setHighlightedCell({ row: null, col: null });
                }
            } else {
                setHighlightedCell({ row: null, col: null });
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [cellSearchTerm, itCodigos]);

    return (
        <Modal
            show={show}
            onHide={handleCancel}
            size="xl"
            centered
            dialogClassName="portfolio-matrix-modal"
        >
            <Modal.Header className="bg-gradient-to-r from-slate-700 to-slate-600 text-white" closeButton>
                <Modal.Title>Configurar Iniciativas Tecnológicas Correlatas</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '75vh', overflow: 'hidden', padding: 0 }}>
                {isLoading ? (
                    <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                        <p className="mt-2 text-gray-600">Carregando configuração...</p>
                    </div>
                ) : itCodigos.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        Nenhuma iniciativa tecnológica disponível
                    </div>
                ) : (
                    <>
                        <div className="portfolio-matrix-toolbar">
                            <div className="toolbar-search-group">
                                <div className="toolbar-search">
                                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                        ref={cellSearchInputRef}
                                        type="text"
                                        value={cellSearchTerm}
                                        onChange={(e) => setCellSearchTerm(e.target.value)}
                                        placeholder="Buscar célula (ex: IT-10×IT-20)..."
                                        className="search-input"
                                    />
                                    {cellSearchTerm && (
                                        <button
                                            onClick={() => {
                                                setHighlightedCell({ row: null, col: null });
                                                setCellSearchTerm('');
                                            }}
                                            className="clear-search-button"
                                            title="Limpar busca"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <button onClick={handleClearAll} className="clear-all-button" title="Limpar todas as seleções">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Limpar Tudo
                            </button>
                        </div>

                        <div className="portfolio-matrix-wrapper">
                            <div className="portfolio-matrix-scroll-container">
                                <table className="portfolio-matrix-table">
                                    <thead>
                                        <tr>
                                            <th className="sticky-corner">Código IT</th>
                                            {itCodigos.map(codigo => (
                                                <ColumnHeader
                                                    key={codigo}
                                                    codigo={codigo}
                                                />
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itCodigos.map(rowCodigo => (
                                            <tr key={rowCodigo}>
                                                <RowHeader
                                                    codigo={rowCodigo}
                                                    nome={iniciativasTecnologicas[rowCodigo]}
                                                    isSearchHighlighted={highlightedIT === rowCodigo}
                                                />
                                                {itCodigos.map(colCodigo => (
                                                    <MatrixCell
                                                        key={colCodigo}
                                                        rowId={rowCodigo}
                                                        colId={colCodigo}
                                                        isChecked={selectionMatrix[rowCodigo]?.[colCodigo]}
                                                        isDiagonal={rowCodigo === colCodigo}
                                                        onToggle={() => toggleSelection(rowCodigo, colCodigo)}
                                                        isHighlightedCell={highlightedCell.row === rowCodigo && highlightedCell.col === colCodigo}
                                                    />
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>

            {!isLoading && (
                <Modal.Footer className="bg-gray-50 border-t border-gray-200">
                    <Button
                        style={{
                            backgroundColor: '#dc3545',
                            borderColor: '#dc3545',
                            padding: '12px 50px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            minWidth: '150px',
                            fontSize: '16px'
                        }}
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-whote border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancelar
                    </Button>
                    <Button
                        style={{
                            backgroundColor: '#003B6B',
                            borderColor: '#003B6B',
                            padding: '12px 50px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                            minWidth: '150px',
                            fontSize: '16px',
                        }}
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default ITMatrixModal;
